import React, { useCallback, useRef } from 'react';
import {
  Platform,
  Pressable,
  type AccessibilityRole,
  type Insets,
  type PressableProps,
} from 'react-native';
import {
  type HeaderTooltipPlacement,
  useHeaderTooltip,
} from './HeaderTooltipProvider';
import { useAppSelector } from '../../../redux/helper';
import { selectTooltipsDisabled } from '../../../redux/SettingsSlice';

type HeaderIconButtonProps = {
  tooltip: string;
  children: React.ReactNode;
  onPress?: () => void;
  onLongPress?: () => void;
  disabled?: boolean;
  hitSlop?: Insets | number;
  style?: PressableProps['style'];
  accessibilityLabel?: string;
  accessibilityRole?: AccessibilityRole;
  tooltipDisabled?: boolean;
  tooltipPlacement?: HeaderTooltipPlacement;
  tooltipOffset?: number;
};

const HeaderIconButton: React.FC<HeaderIconButtonProps> = ({
  tooltip,
  children,
  onPress,
  onLongPress,
  disabled,
  hitSlop,
  style,
  accessibilityLabel,
  accessibilityRole = 'button',
  tooltipDisabled,
  tooltipPlacement = 'bottom',
  tooltipOffset,
}) => {
  const buttonRef = useRef<any>(null);
  const tooltipApi = useHeaderTooltip();
  const tooltipsDisabledBySetting = useAppSelector(selectTooltipsDisabled);
  const tooltipIdRef = useRef(
    `header-tooltip-${Math.random().toString(36).slice(2)}`,
  );
  const resolvedLabel = accessibilityLabel || tooltip;
  const canShowTooltip = Boolean(
    tooltipApi && tooltip && !tooltipDisabled && !tooltipsDisabledBySetting,
  );

  const showTooltip = useCallback(() => {
    if (!canShowTooltip) return;
    tooltipApi?.showTooltip({
      id: tooltipIdRef.current,
      title: tooltip,
      targetRef: buttonRef,
      placement: tooltipPlacement,
      offset: tooltipOffset,
    });
  }, [canShowTooltip, tooltip, tooltipApi, tooltipOffset, tooltipPlacement]);

  const hideTooltip = useCallback(
    (delayMs?: number) => {
      tooltipApi?.hideTooltip(tooltipIdRef.current, delayMs);
    },
    [tooltipApi],
  );

  const handlePress = useCallback(() => {
    tooltipApi?.hideActiveTooltip();
    onPress?.();
  }, [onPress, tooltipApi]);

  const handleLongPress = useCallback(() => {
    showTooltip();
    onLongPress?.();
  }, [onLongPress, showTooltip]);

  return (
    <Pressable
      ref={buttonRef}
      onPress={handlePress}
      onLongPress={handleLongPress}
      onHoverIn={Platform.OS === 'web' ? showTooltip : undefined}
      onHoverOut={Platform.OS === 'web' ? () => hideTooltip(10) : undefined}
      // onFocus={Platform.OS === 'web' ? showTooltip : undefined}
      onBlur={Platform.OS === 'web' ? () => hideTooltip(0) : undefined}
      disabled={disabled}
      hitSlop={hitSlop}
      accessibilityRole={accessibilityRole}
      accessibilityLabel={resolvedLabel}
      style={style}>
      {children}
    </Pressable>
  );
};

export default HeaderIconButton;
